import React, { useEffect, useRef, useState } from 'react';
import { HeroImage as HeroImageType } from './types';
import { MobileImageCarousel } from './MobileImageCarousel';
import { MAX_VEHICLE_HERO_IMAGES } from '../../constants/vehicleGallery';
import { HeroImage } from '../ui/HeroImage';
import { focalToObjectPosition } from '../../constants/imageCropPresets';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import './photo-stack.css';

interface VehicleHeroGalleryProps {
  images: HeroImageType[];
  isOwner: boolean;
  onImageClick: (index: number) => void;
  onEditImage: (index: number) => void;
  onDeleteImage: (index: number) => void;
  onMoveImage: (fromIndex: number, toIndex: number) => void;
  onAddImage: () => void;
  /** Car-show / iPad kiosk layout — large swipeable gallery */
  showcaseMode?: boolean;
  /** When true, gallery fills a sticky side panel (landscape iPad) */
  showcasePanel?: boolean;
  /** Nested in the build-page photo card — no extra shell/border/scrim. */
  embedded?: boolean;
}

type CollageCell = {
  index: number;
  className: string;
  /** Banner aspect for the media frame */
  frameClass: string;
};

const PHOTO_STACK_DEFAULTS = {
  '--photo-drag-x': '0px',
  '--photo-main-rotation': '0deg',
  '--photo-main-opacity': '1',
  '--photo-next-brightness': '0.76',
  '--photo-next-opacity': '0.84',
  '--photo-previous-brightness': '0.7',
  '--photo-next-stack-x': '14px',
  '--photo-next-stack-y': '-12px',
  '--photo-next-stack-rotation': '-2.3deg',
  '--photo-depth-stack-x': '10px',
  '--photo-depth-stack-y': '-20px',
  '--photo-depth-stack-rotation': '3deg',
  '--photo-depth-opacity': '0.5',
  '--photo-previous-stack-x': '-22px',
  '--photo-previous-stack-y': '-14px',
  '--photo-previous-stack-rotation': '-4.2deg',
  '--photo-previous-opacity': '0',
} as const;

function resetPhotoStackStyles(deck: HTMLDivElement | null) {
  if (!deck) return;
  Object.entries(PHOTO_STACK_DEFAULTS).forEach(([property, value]) => {
    deck.style.setProperty(property, value);
  });
  deck.style.setProperty('--photo-tilt-x', '0deg');
  deck.style.setProperty('--photo-tilt-y', '0deg');
}

/**
 * Banner-aspect collage patterns by photo count (tablet/desktop).
 * All frames use wide ratios (2:1 / 21:9 / 16:9) rather than squares.
 */
function collageCells(count: number): { gridClass: string; cells: CollageCell[] } {
  const banner = 'aspect-[2/1]';
  const wide = 'aspect-[21/9]';
  const cinema = 'aspect-[16/9]';

  switch (count) {
    case 1:
      return {
        gridClass: 'grid-cols-1',
        cells: [{ index: 0, className: 'col-span-1', frameClass: wide }],
      };
    case 2:
      return {
        gridClass: 'grid-cols-2',
        cells: [
          { index: 0, className: 'col-span-1', frameClass: banner },
          { index: 1, className: 'col-span-1', frameClass: banner },
        ],
      };
    case 3:
      return {
        gridClass: 'grid-cols-3 grid-rows-2',
        cells: [
          {
            index: 0,
            className: 'col-span-2 row-span-2',
            frameClass: 'h-full min-h-[14rem] aspect-auto',
          },
          { index: 1, className: 'col-span-1', frameClass: banner },
          { index: 2, className: 'col-span-1', frameClass: banner },
        ],
      };
    case 4:
      return {
        gridClass: 'grid-cols-2',
        cells: [0, 1, 2, 3].map((index) => ({
          index,
          className: 'col-span-1',
          frameClass: cinema,
        })),
      };
    case 5:
      return {
        gridClass: 'grid-cols-6',
        cells: [
          { index: 0, className: 'col-span-6', frameClass: wide },
          { index: 1, className: 'col-span-3', frameClass: banner },
          { index: 2, className: 'col-span-3', frameClass: banner },
          { index: 3, className: 'col-span-3', frameClass: banner },
          { index: 4, className: 'col-span-3', frameClass: banner },
        ],
      };
    case 6:
      return {
        gridClass: 'grid-cols-3',
        cells: [0, 1, 2, 3, 4, 5].map((index) => ({
          index,
          className: 'col-span-1',
          frameClass: cinema,
        })),
      };
    case 7:
      return {
        gridClass: 'grid-cols-6',
        cells: [
          { index: 0, className: 'col-span-6', frameClass: wide },
          ...[1, 2, 3, 4, 5, 6].map((index) => ({
            index,
            className: 'col-span-2',
            frameClass: cinema,
          })),
        ],
      };
    case 8:
    default:
      return {
        gridClass: 'grid-cols-4',
        cells: Array.from({ length: Math.min(count, 8) }, (_, index) => ({
          index,
          className: 'col-span-1',
          frameClass: cinema,
        })),
      };
  }
}

function GalleryTile({
  image,
  index,
  total,
  frameClass,
  isOwner,
  onImageClick,
  onEditImage,
  onDeleteImage,
  onMoveImage,
}: {
  image: HeroImageType;
  index: number;
  total: number;
  frameClass: string;
  isOwner: boolean;
  onImageClick: (index: number) => void;
  onEditImage: (index: number) => void;
  onDeleteImage: (index: number) => void;
  onMoveImage: (fromIndex: number, toIndex: number) => void;
}) {
  return (
    <div
      className="relative group cursor-pointer min-w-0 h-full"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onImageClick(index);
      }}
    >
      <div
        className={`${frameClass} h-full bg-[#111] overflow-hidden`}
        role="button"
        tabIndex={0}
        aria-label={`Enlarge ${image.title || `photo ${index + 1}`}`}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onImageClick(index);
          }
        }}
      >
        <HeroImage
          src={image.src}
          alt={image.alt}
          position={focalToObjectPosition(image.focalX, image.focalY)}
          priority={index === 0}
          className="transition-transform duration-200 group-hover:scale-[1.03]"
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-white text-sm font-medium truncate backdrop-blur-sm bg-black/30 px-2 py-1 rounded">
            {image.title}
          </h3>
        </div>
      </div>

      {isOwner ? (
        <div
          className="showcase-gallery__tools absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
          onClick={(e) => e.stopPropagation()}
        >
          {index > 0 ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveImage(index, index - 1);
              }}
              className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30 z-30"
              title="Move left"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          ) : null}
          {index < total - 1 ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMoveImage(index, index + 1);
              }}
              className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30 z-30"
              title="Move right"
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          ) : null}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEditImage(index);
            }}
            className="p-1.5 bg-[#141414]/80 backdrop-blur-sm hover:bg-white/[0.06] text-white rounded-lg transition-colors border border-white/[0.08] z-30"
            title="Edit image"
            type="button"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDeleteImage(index);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="p-1.5 bg-red-500/80 backdrop-blur-sm hover:bg-red-500 text-white rounded-lg transition-colors border border-red-400/30 z-30 cursor-pointer"
            title="Delete image"
            type="button"
            style={{ pointerEvents: 'auto' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ) : null}
    </div>
  );
}

export const VehicleHeroGallery: React.FC<VehicleHeroGalleryProps> = ({
  images,
  isOwner,
  onImageClick,
  onEditImage,
  onDeleteImage,
  onMoveImage,
  onAddImage,
  showcaseMode = false,
  showcasePanel = false,
  embedded = false,
}) => {
  const photoStack = useRef<HTMLDivElement>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const selectedIndex = Math.max(
    0,
    images.findIndex((image) => (image.id || image.src) === selectedPhoto)
  );
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false);
  const [dragDirection, setDragDirection] = useState<'next' | 'previous' | null>(null);
  const [swipePhase, setSwipePhase] = useState<'settling' | 'committing' | null>(null);
  const gesture = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    axis: 'x' | 'y' | null;
    active: boolean;
    width: number;
    lastX: number;
    lastAt: number;
    velocityX: number;
    direction: 'next' | 'previous' | null;
    dragX: number;
  } | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleFrame = useRef<number | null>(null);
  const lostCaptureTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queuedNavigationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigationQueue = useRef<Array<1 | -1>>([]);
  const navigateRef = useRef<((delta: 1 | -1) => void) | null>(null);
  const resolvePointerRelease = useRef<((releaseX?: number) => void) | null>(null);
  const suppressClick = useRef(false);
  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (settleFrame.current !== null) cancelAnimationFrame(settleFrame.current);
      if (lostCaptureTimer.current) clearTimeout(lostCaptureTimer.current);
      if (queuedNavigationTimer.current) clearTimeout(queuedNavigationTimer.current);
    },
    []
  );
  useEffect(() => {
    const abortExternalGesture = (event: Event) => {
      const current = gesture.current;
      if (!current) return;

      // A captured pointerup/pointercancel is delivered to the deck first. Let
      // that handler resolve the gesture; this listener covers releases after
      // capture was lost or the pointer left the browser surface.
      if (event.type === 'pointerup' || event.type === 'pointercancel') {
        const target = event.target;
        if (target instanceof Node && photoStack.current?.contains(target)) return;
      }

      if (event.type === 'pointerup') {
        const pointerEvent = event as PointerEvent;
        if (
          pointerEvent.pointerId &&
          current.pointerId &&
          pointerEvent.pointerId !== current.pointerId
        )
          return;
        resolvePointerRelease.current?.(pointerEvent.clientX);
        return;
      }

      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (settleFrame.current !== null) cancelAnimationFrame(settleFrame.current);
      if (lostCaptureTimer.current) clearTimeout(lostCaptureTimer.current);
      if (queuedNavigationTimer.current) clearTimeout(queuedNavigationTimer.current);
      settleTimer.current = null;
      settleFrame.current = null;
      lostCaptureTimer.current = null;
      queuedNavigationTimer.current = null;
      navigationQueue.current = [];
      gesture.current = null;
      setDragging(false);
      setSettling(false);
      setDragDirection(null);
      setSwipePhase(null);
      resetPhotoStackStyles(photoStack.current);
    };

    window.addEventListener('pointerup', abortExternalGesture);
    window.addEventListener('pointercancel', abortExternalGesture);
    window.addEventListener('blur', abortExternalGesture);
    document.addEventListener('visibilitychange', abortExternalGesture);
    return () => {
      window.removeEventListener('pointerup', abortExternalGesture);
      window.removeEventListener('pointercancel', abortExternalGesture);
      window.removeEventListener('blur', abortExternalGesture);
      document.removeEventListener('visibilitychange', abortExternalGesture);
    };
  }, []);
  // Side-panel showcase stays swipeable; normal build pages: carousel on mobile only.
  const forceCarousel = showcasePanel;
  const carouselSize = showcasePanel ? 'showcase' : showcaseMode ? 'tablet' : 'default';
  const autoPlay = !isOwner;
  const collage = collageCells(images.length);
  const showAddSlot = images.length > 0 && images.length < MAX_VEHICLE_HERO_IMAGES && isOwner;

  const shellClass = showcasePanel
    ? 'h-full bg-[#0c0c0c]/50 border-b lg:border-b-0 lg:border-r border-white/[0.06] flex flex-col'
    : embedded
      ? ''
      : 'bg-[#0c0c0c]/50 border-b border-white/[0.06]';

  const innerClass = showcasePanel
    ? 'flex-1 flex flex-col px-4 sm:px-6 py-4 lg:py-6 min-h-0'
    : embedded
      ? 'px-4 sm:px-5 py-4 sm:py-5'
      : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8';

  if (embedded && images.length > 0) {
    const selected = images[selectedIndex];
    const applyDragPosition = (nextX: number) => {
      const deck = photoStack.current;
      if (!deck) return;
      const width = gesture.current?.width || deck.clientWidth || 1;
      const progress = Math.min(1, Math.abs(nextX) / Math.max(width * 0.62, 1));
      const goingNext = nextX < 0;
      const nextProgress = goingNext ? progress : 0;
      const previousProgress = nextX > 0 ? progress : 0;
      const mainRotation = nextX === 0 ? 0 : goingNext ? progress * -4.5 : progress * 2.8;
      // The outgoing card should fully clear before the incoming card is
      // promoted. Keeping opacity coupled to the travel prevents a visible
      // pop at the end of a fast swipe and still feels natural on a slow drag.
      const mainOpacity = Math.max(0, 1 - progress);
      // The card behind the incoming image yields with the direction of the
      // gesture. This keeps the whole deck coherent instead of leaving the
      // second layer frozen while the top two cards travel.
      const nextStackX = goingNext
        ? (1 - nextProgress) * 14
        : 14 - previousProgress * 4;
      const nextStackY = goingNext
        ? (1 - nextProgress) * -12
        : -12 - previousProgress * 8;
      const nextStackRotation = goingNext
        ? (1 - nextProgress) * -2.3
        : -2.3 + previousProgress * 5.3;
      // As the next card is promoted, the deeper card travels into the
      // vacated layer. On commit it already occupies its final resting place.
      const depthStackX = goingNext ? 10 + nextProgress * 4 : 10 - previousProgress * 6;
      const depthStackY = goingNext ? -20 + nextProgress * 8 : -20 - previousProgress * 8;
      const depthStackRotation = goingNext ? 3 - nextProgress * 5.3 : 3 + previousProgress * 3;
      const depthOpacity = goingNext
        ? 0.5 + nextProgress * 0.34
        : 0.5 - previousProgress * 0.14;
      const nextOpacity = goingNext
        ? 0.84 + nextProgress * 0.16
        : 0.84 - previousProgress * 0.34;
      const previousStackX = (1 - previousProgress) * -22;
      const previousStackY = (1 - previousProgress) * -14;
      const previousStackRotation = (1 - previousProgress) * -4.2;
      deck.style.setProperty('--photo-drag-x', `${nextX}px`);
      if (gesture.current) gesture.current.dragX = nextX;
      deck.style.setProperty('--photo-main-rotation', `${mainRotation}deg`);
      deck.style.setProperty('--photo-main-opacity', `${mainOpacity}`);
      deck.style.setProperty('--photo-next-brightness', `${0.76 + nextProgress * 0.24}`);
      deck.style.setProperty('--photo-next-opacity', `${nextOpacity}`);
      deck.style.setProperty('--photo-previous-brightness', `${0.7 + previousProgress * 0.3}`);
      deck.style.setProperty('--photo-next-stack-x', `${nextStackX}px`);
      deck.style.setProperty('--photo-next-stack-y', `${nextStackY}px`);
      deck.style.setProperty('--photo-next-stack-rotation', `${nextStackRotation}deg`);
      deck.style.setProperty('--photo-depth-stack-x', `${depthStackX}px`);
      deck.style.setProperty('--photo-depth-stack-y', `${depthStackY}px`);
      deck.style.setProperty('--photo-depth-stack-rotation', `${depthStackRotation}deg`);
      deck.style.setProperty('--photo-depth-opacity', `${depthOpacity}`);
      deck.style.setProperty('--photo-previous-stack-x', `${previousStackX}px`);
      deck.style.setProperty('--photo-previous-stack-y', `${previousStackY}px`);
      deck.style.setProperty('--photo-previous-stack-rotation', `${previousStackRotation}deg`);
      deck.style.setProperty(
        '--photo-previous-opacity',
        `${Math.min(1, previousProgress * 1.25)}`
      );
    };
    const resetDragPosition = () => {
      applyDragPosition(0);
      photoStack.current?.style.setProperty('--photo-tilt-x', '0deg');
      photoStack.current?.style.setProperty('--photo-tilt-y', '0deg');
    };
    const resetStackPosition = () => resetPhotoStackStyles(photoStack.current);
    const clearLostCaptureTimer = () => {
      if (lostCaptureTimer.current) clearTimeout(lostCaptureTimer.current);
      lostCaptureTimer.current = null;
    };
    const clearQueuedNavigation = () => {
      if (queuedNavigationTimer.current) clearTimeout(queuedNavigationTimer.current);
      queuedNavigationTimer.current = null;
      navigationQueue.current = [];
    };
    const finishGesture = (resetPosition = true) => {
      clearLostCaptureTimer();
      gesture.current = null;
      setDragging(false);
      setSettling(false);
      setDragDirection(null);
      setSwipePhase(null);
      if (resetPosition) resetDragPosition();
      if (navigationQueue.current.length && !queuedNavigationTimer.current) {
        const nextNavigation = navigationQueue.current.shift();
        if (nextNavigation) {
          queuedNavigationTimer.current = setTimeout(() => {
            queuedNavigationTimer.current = null;
            if (gesture.current) {
              navigationQueue.current.unshift(nextNavigation);
              return;
            }
            navigateRef.current?.(nextNavigation);
          }, 0);
        }
      }
    };
    const cancelGesture = () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (settleFrame.current !== null) cancelAnimationFrame(settleFrame.current);
      clearLostCaptureTimer();
      clearQueuedNavigation();
      settleTimer.current = null;
      settleFrame.current = null;
      finishGesture();
    };
    const animateStack = (
      startX: number,
      targetX: number,
      duration: number,
      onComplete: () => void
    ) => {
      if (!duration) {
        applyDragPosition(targetX);
        onComplete();
        return;
      }
      const startedAt = performance.now();
      const tick = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        applyDragPosition(startX + (targetX - startX) * eased);
        if (progress < 1) {
          settleFrame.current = requestAnimationFrame(tick);
          return;
        }
        settleFrame.current = null;
        onComplete();
      };
      settleFrame.current = requestAnimationFrame(tick);
    };
    const commitSwipe = (delta: number, duration: number) => {
      const next = images[(selectedIndex + delta + images.length) % images.length];
      setSwipePhase('committing');
      setSelectedPhoto(next.id || next.src);
      if (!duration) {
        resetStackPosition();
        finishGesture(false);
        return;
      }
      settleFrame.current = requestAnimationFrame(() => {
        settleFrame.current = null;
        // Promote the already settled incoming card in the same frame that the
        // active image is reset. The card layers then finish their own short
        // handoff without inheriting the outgoing card's transform.
        resetStackPosition();
        settleTimer.current = setTimeout(() => {
          settleTimer.current = null;
          finishGesture(false);
        }, 280);
      });
    };
    const settleGesture = (delta: number) => {
      if (gesture.current) gesture.current.active = false;
      clearLostCaptureTimer();
      if (settleTimer.current) clearTimeout(settleTimer.current);
      settleTimer.current = null;
      if (settleFrame.current !== null) cancelAnimationFrame(settleFrame.current);
      settleFrame.current = null;
      const deck = photoStack.current;
      const width = gesture.current?.width || deck?.clientWidth || 1;
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      const duration = reduced || process.env.NODE_ENV === 'test' ? 0 : 360;
      const startX = gesture.current?.dragX || 0;
      if (!delta) {
        setSettling(true);
        setSwipePhase('settling');
        animateStack(startX, 0, duration, () => finishGesture(false));
        return;
      }
      const exitX = delta > 0 ? -Math.max(140, width * 0.82) : Math.max(140, width * 0.82);
      setDragDirection(delta > 0 ? 'next' : 'previous');
      setDragging(false);
      setSettling(true);
      setSwipePhase('settling');
      animateStack(startX, exitX, duration, () => commitSwipe(delta, duration));
    };
    const releaseGesture = (releaseX?: number) => {
      const current = gesture.current;
      if (!current || !current.active) return;
      if (current.axis !== 'x') {
        cancelGesture();
        return;
      }
      const distance = current.dragX || (releaseX ?? current.lastX) - current.startX;
      const threshold = Math.max(44, current.width * 0.16);
      const flick = Math.abs(current.velocityX) > 0.42 && Math.abs(distance) > 20;
      const delta = Math.abs(distance) >= threshold || flick ? (distance < 0 ? 1 : -1) : 0;
      settleGesture(delta);
    };
    resolvePointerRelease.current = releaseGesture;
    const navigate = (delta: 1 | -1) => {
      if (images.length < 2) return;
      if (gesture.current) {
        // A rapid run of taps should advance one card per intent rather than
        // repeatedly cancelling the same in-flight settle.
        if (navigationQueue.current.length < 8) navigationQueue.current.push(delta);
        return;
      }
      const deck = photoStack.current;
      const width = deck?.clientWidth || 1;
      gesture.current = {
        pointerId: 0,
        startX: 0,
        startY: 0,
        axis: 'x',
        active: false,
        width,
        lastX: 0,
        lastAt: performance.now(),
        velocityX: 0,
        direction: delta > 0 ? 'next' : 'previous',
        dragX: 0,
      };
      settleGesture(delta);
    };
    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        images.length < 2 ||
        gesture.current?.active ||
        (event.pointerType === 'mouse' && event.button !== 0) ||
        (event.target as HTMLElement).closest('button')
      )
        return;
      clearQueuedNavigation();
      if (settleTimer.current) clearTimeout(settleTimer.current);
      if (settleFrame.current !== null) cancelAnimationFrame(settleFrame.current);
      clearLostCaptureTimer();
      settleTimer.current = null;
      settleFrame.current = null;
      suppressClick.current = false;
      gesture.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        axis: null,
        active: true,
        width: event.currentTarget.clientWidth,
        lastX: event.clientX,
        lastAt: performance.now(),
        velocityX: 0,
        direction: null,
        dragX: 0,
      };
      setDragging(true);
      setSettling(false);
      setDragDirection(null);
      setSwipePhase(null);
      resetDragPosition();
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        // Pointer capture can fail when a browser ends the contact between
        // pointerdown and this call. The window-level lifecycle guard still
        // resolves the gesture in that case.
      }
    };
    navigateRef.current = navigate;
    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        event.pointerType !== 'touch' &&
        !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
      ) {
        const rect = event.currentTarget.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          event.currentTarget.style.setProperty(
            '--photo-tilt-x',
            `${(0.5 - (event.clientY - rect.top) / rect.height) * 3}deg`
          );
          event.currentTarget.style.setProperty(
            '--photo-tilt-y',
            `${((event.clientX - rect.left) / rect.width - 0.5) * 3}deg`
          );
        }
      }
      const current = gesture.current;
      if (
        !current ||
        !current.active ||
        (event.pointerId && current.pointerId && current.pointerId !== event.pointerId)
      )
        return;
      const dx = event.clientX - current.startX;
      const dy = event.clientY - current.startY;
      const now = performance.now();
      const elapsed = Math.max(1, now - current.lastAt);
      current.velocityX = (event.clientX - current.lastX) / elapsed;
      current.lastX = event.clientX;
      current.lastAt = now;
      if (!current.axis && Math.hypot(dx, dy) > 6) {
        if (Math.abs(dy) > Math.abs(dx)) {
          current.axis = 'y';
          current.active = false;
          suppressClick.current = true;
          finishGesture();
          return;
        }
        current.axis = 'x';
        suppressClick.current = true;
      }
      if (current.axis !== 'x') return;
      event.preventDefault();
      // JSDOM and a few embedded hosts can report a zero-width region. Keep
      // the direct-manipulation signal intact there instead of collapsing the
      // drag to zero; real layouts still clamp to the visible deck width.
      const nextX =
        current.width > 1
          ? Math.max(-current.width * 0.86, Math.min(current.width * 0.86, dx))
          : dx;
      applyDragPosition(nextX);
      const nextDirection = nextX < 0 ? 'next' : nextX > 0 ? 'previous' : null;
      if (current.direction !== nextDirection) {
        current.direction = nextDirection;
        setDragDirection(nextDirection);
      }
    };
    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
      const current = gesture.current;
      if (
        !current ||
        (event.pointerId && current.pointerId && current.pointerId !== event.pointerId)
      )
        return;
      try {
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
          event.currentTarget.releasePointerCapture?.(event.pointerId);
        }
      } catch {
        // The pointer may already have been released by the browser.
      }
      if (current.axis !== 'x') {
        finishGesture();
        return;
      }
      releaseGesture(event.clientX);
    };
    const handlePointerCancel = (event: React.PointerEvent<HTMLDivElement>) => {
      const current = gesture.current;
      if (
        !current ||
        (event.pointerId && current.pointerId && current.pointerId !== event.pointerId)
      )
        return;
      if (current.axis === 'x') settleGesture(0);
      else cancelGesture();
    };
    const handleLostPointerCapture = () => {
      const current = gesture.current;
      if (!current?.active) return;
      clearLostCaptureTimer();
      const capturedGesture = current;
      // Some embedded browsers emit lostpointercapture without a matching
      // pointerup. Give a real release a short window to arrive, then clear
      // the interaction so the card can never remain attached to the pointer.
      lostCaptureTimer.current = setTimeout(() => {
        lostCaptureTimer.current = null;
        if (gesture.current === capturedGesture && capturedGesture.active) {
          cancelGesture();
        }
      }, 120);
    };
    return (
      <div
        className="showcase-gallery showcase-gallery--stack"
        aria-label="Build gallery"
        data-photo-count={images.length}
      >
        <div
          className="showcase-gallery__deck"
          ref={photoStack}
          role="region"
          aria-label="Photo stack"
          tabIndex={0}
          data-dragging={dragging}
          data-swipe-settling={settling}
          data-swipe-direction={dragDirection || undefined}
          data-swipe-phase={swipePhase || undefined}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onLostPointerCapture={handleLostPointerCapture}
          onClickCapture={(event) => {
            if (suppressClick.current) {
              suppressClick.current = false;
              // Suppress the synthetic click from a completed drag, but do
              // not eat a deliberate control tap that happens immediately
              // afterward (back card, owner tools, or another action button).
              if (!(event.target as HTMLElement).closest('button')) {
                event.preventDefault();
                event.stopPropagation();
              }
            }
          }}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
              event.preventDefault();
              navigate(event.key === 'ArrowLeft' ? -1 : 1);
            }
          }}
          onPointerLeave={(event) => {
            event.currentTarget.style.setProperty('--photo-tilt-x', '0deg');
            event.currentTarget.style.setProperty('--photo-tilt-y', '0deg');
          }}
        >
          {images.length > 1 ? (
            <button
              type="button"
              className="showcase-gallery__back-photo"
              data-direction="previous"
              aria-label={`Bring forward ${images[(selectedIndex - 1 + images.length) % images.length].title || `photo ${((selectedIndex - 1 + images.length) % images.length) + 1}`}`}
              onClick={() => navigate(-1)}
            >
              <img
                src={images[(selectedIndex - 1 + images.length) % images.length].src}
                alt=""
                loading="lazy"
                style={{
                  objectPosition: focalToObjectPosition(
                    images[(selectedIndex - 1 + images.length) % images.length].focalX,
                    images[(selectedIndex - 1 + images.length) % images.length].focalY
                  ),
                }}
                />
            </button>
          ) : null}
          {Array.from({ length: Math.min(2, images.length - 1) }, (_, depth) => {
            const index = (selectedIndex + depth + 1) % images.length;
            const image = images[index];
            return (
              <button
                type="button"
                className="showcase-gallery__back-photo"
                data-depth={depth + 1}
                data-direction="next"
                key={image.id || image.src}
                aria-label={`Bring forward ${image.title || `photo ${index + 1}`}`}
                onClick={() => setSelectedPhoto(image.id || image.src)}
              >
                <img
                  src={image.src}
                  alt=""
                  loading="lazy"
                  style={{ objectPosition: focalToObjectPosition(image.focalX, image.focalY) }}
                />
              </button>
            );
          })}
          <div className="showcase-gallery__main">
            <div className="showcase-gallery__sleeve">
              <GalleryTile
                image={selected}
                index={selectedIndex}
                total={images.length}
                frameClass="showcase-gallery__frame"
                isOwner={isOwner}
                onImageClick={onImageClick}
                onEditImage={onEditImage}
                onDeleteImage={onDeleteImage}
                onMoveImage={onMoveImage}
              />
            </div>
          </div>
        </div>
        <div className="showcase-gallery__selection">
          <p className="showcase-gallery__photo-title">
            {selected.title || 'Build photographs'}
            <span aria-live="polite">
              {selectedIndex + 1} / {images.length}
            </span>
          </p>
          {images.length > 1 && (
            <div role="group" aria-label="Browse build photos">
              <button type="button" aria-label="Previous photo" onClick={() => navigate(-1)}>
                <ArrowLeftIcon aria-hidden />
              </button>
              <button type="button" aria-label="Next photo" onClick={() => navigate(1)}>
                <ArrowRightIcon aria-hidden />
              </button>
            </div>
          )}
        </div>
        <div className="showcase-gallery__footer">
          <div
            className="showcase-gallery__thumbnails"
            role="group"
            aria-label="Choose a build photo"
          >
            {images.map((image, index) => (
              <button
                type="button"
                key={`${image.src}-${index}`}
                aria-label={`Show ${image.title || `photo ${index + 1}`}`}
                aria-pressed={selectedIndex === index}
                onClick={() => setSelectedPhoto(image.id || image.src)}
              >
                <img
                  src={image.src}
                  alt=""
                  loading="lazy"
                  style={{ objectPosition: focalToObjectPosition(image.focalX, image.focalY) }}
                />
              </button>
            ))}
          </div>
          {isOwner && images.length < MAX_VEHICLE_HERO_IMAGES ? (
            <button type="button" className="btn-secondary btn-sm" onClick={onAddImage}>
              Add photo
            </button>
          ) : (
            <button
              type="button"
              className="showcase-gallery__enlarge"
              onClick={() => onImageClick(selectedIndex)}
            >
              View photos
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div className={innerClass}>
        {/* Carousel: phones only (and sticky showcase side panel) */}
        <div className={forceCarousel ? 'block' : 'block md:hidden'}>
          <MobileImageCarousel
            images={images}
            isOwner={isOwner}
            onImageClick={onImageClick}
            onEditImage={onEditImage}
            onDeleteImage={onDeleteImage}
            onMoveImage={onMoveImage}
            onAddImage={onAddImage}
            size={carouselSize}
            autoPlay={autoPlay}
          />
          {showcaseMode && !isOwner && images.length > 0 && (
            <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 md:hidden">
              Swipe for more · Tap to enlarge
            </p>
          )}
        </div>

        {/* Banner collage: tablet and up — single rounded rectangle, no gaps between photos */}
        <div
          className={forceCarousel ? 'hidden' : 'hidden md:block'}
          data-collage-count={images.length}
        >
          {images.length > 0 ? (
            <div
              className={`grid gap-0 overflow-hidden rounded-xl border border-white/[0.07] bg-[#111] ${collage.gridClass}`}
            >
              {collage.cells.map((cell) => {
                const image = images[cell.index];
                if (!image) return null;
                return (
                  <div key={`${image.src}-${cell.index}`} className={`min-w-0 ${cell.className}`}>
                    <GalleryTile
                      image={image}
                      index={cell.index}
                      total={images.length}
                      frameClass={cell.frameClass}
                      isOwner={isOwner}
                      onImageClick={onImageClick}
                      onEditImage={onEditImage}
                      onDeleteImage={onDeleteImage}
                      onMoveImage={onMoveImage}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}

          {showAddSlot ? (
            <button
              type="button"
              onClick={onAddImage}
              className="mt-3 w-full aspect-[2/1] max-h-36 bg-[#0c0c0c]/50 rounded-xl border-2 border-dashed border-white/[0.08] hover:border-orange-500/50 cursor-pointer transition-all duration-200 flex items-center justify-center group hover:bg-white/[0.04]/40"
            >
              <div className="text-center px-3">
                <svg
                  className="w-8 h-8 text-gray-500 group-hover:text-orange-400 mx-auto mb-2 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <p className="text-meta group-hover:text-orange-400 transition-colors">Add photo</p>
                <p className="text-[11px] text-gray-600 mt-1 tabular-nums">
                  {images.length}/{MAX_VEHICLE_HERO_IMAGES}
                </p>
              </div>
            </button>
          ) : null}

          {images.length === 0 && !isOwner ? (
            <div className="text-center py-12 card rounded-xl">
              <svg
                className="w-16 h-16 text-gray-500 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-muted">No photos available</p>
            </div>
          ) : null}

          {images.length === 0 && isOwner ? (
            <div className="text-center py-12 card rounded-xl">
              <p className="text-muted text-sm mb-4">No photos yet</p>
              <button type="button" onClick={onAddImage} className="btn-primary">
                Add photos
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
